import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { rolesAPI, type Role, type CreateRoleRequest, type CreatePermissionRequest, usersAPI, campusAPI } from "../api";
import { User, Campus } from "../types";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Shield, Users, Lock, UserPlus, Plus, Edit, Trash2, Search, ChevronDown, ChevronUp, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";

export function RolesManagementPage() {
  const [activeTab, setActiveTab] = useState("roles");
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [assignRoleDialogOpen, setAssignRoleDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedCampus, setSelectedCampus] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [permissionSearchTerm, setPermissionSearchTerm] = useState("");
  const [userSelectorSearch, setUserSelectorSearch] = useState("");
  const [expandedResource, setExpandedResource] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Form states for creating/editing role
  const [roleName, setRoleName] = useState("");
  const [roleDisplayName, setRoleDisplayName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [roleScope, setRoleScope] = useState<"global" | "campus">("campus");
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());

  // Form states for creating permission
  const [permResource, setPermResource] = useState("");
  const [permAction, setPermAction] = useState("");
  const [permDescription, setPermDescription] = useState("");

  // Fetch roles
  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesAPI.getRoles(),
  });

  // Fetch permissions
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => rolesAPI.getPermissions(),
  });

  // Fetch all users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersAPI.getAllUsers(),
  });

  // Fetch all campuses
  const { data: campusesData } = useQuery({
    queryKey: ['campuses'],
    queryFn: () => campusAPI.getAllCampuses(),
  });

  // Create role mutation
  const createRoleMutation = useMutation({
    mutationFn: (data: CreateRoleRequest) => rolesAPI.createRole(data),
    onSuccess: () => {
      toast.success("Role created successfully");
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      resetRoleForm();
      setRoleDialogOpen(false);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to create role");
    },
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ roleId, data }: { roleId: string; data: Partial<Role> }) =>
      rolesAPI.updateRole(roleId, data),
    onSuccess: () => {
      toast.success("Role updated successfully");
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      resetRoleForm();
      setRoleDialogOpen(false);
      setEditingRole(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to update role");
    },
  });

  // Delete role mutation
  const deleteRoleMutation = useMutation({
    mutationFn: (roleId: string) => rolesAPI.deleteRole(roleId),
    onSuccess: () => {
      toast.success("Role deleted successfully");
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setDeleteConfirmOpen(false);
      setRoleToDelete(null);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to delete role");
    },
  });

  // Create permission mutation
  const createPermissionMutation = useMutation({
    mutationFn: (data: CreatePermissionRequest) => rolesAPI.createPermission(data),
    onSuccess: () => {
      toast.success("Permission created successfully");
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      resetPermissionForm();
      setPermissionDialogOpen(false);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to create permission");
    },
  });

  // Assign role mutation
  const assignRoleMutation = useMutation({
    mutationFn: ({ userId, role, campus_id }: { userId: string; role: string; campus_id?: string }) =>
      rolesAPI.assignRole(userId, { role, campus_id }),
    onSuccess: () => {
      toast.success("Role assigned successfully");
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setAssignRoleDialogOpen(false);
      setSelectedUser(null);
      setSelectedRole("");
      setSelectedCampus("");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to assign role");
    },
  });

  const roles = rolesData?.roles || [];
  const permissions = permissionsData?.permissions || {};
  const users = Array.isArray(usersData) ? usersData : [];
  const campuses = Array.isArray(campusesData)
    ? campusesData
    : campusesData?.data
    ? campusesData.data
    : [];

  const resetRoleForm = () => {
    setRoleName("");
    setRoleDisplayName("");
    setRoleDescription("");
    setRoleScope("campus");
    setSelectedPermissions(new Set());
  };

  const resetPermissionForm = () => {
    setPermResource("");
    setPermAction("");
    setPermDescription("");
  };

  const openCreateRoleDialog = () => {
    resetRoleForm();
    setEditingRole(null);
    setRoleDialogOpen(true);
  };

  const openEditRoleDialog = (role: Role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDisplayName(role.display_name);
    setRoleDescription(role.description);
    setRoleScope(role.scope);
    setSelectedPermissions(new Set(role.permissions));
    setRoleDialogOpen(true);
  };

  const handleSaveRole = () => {
    if (!roleDisplayName || !roleDescription) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (editingRole) {
      // Update existing role
      updateRoleMutation.mutate({
        roleId: editingRole._id,
        data: {
          display_name: roleDisplayName,
          description: roleDescription,
          permissions: Array.from(selectedPermissions),
          scope: roleScope,
        },
      });
    } else {
      // Create new role
      if (!roleName) {
        toast.error("Role name is required");
        return;
      }
      createRoleMutation.mutate({
        name: roleName,
        display_name: roleDisplayName,
        description: roleDescription,
        permissions: Array.from(selectedPermissions),
        scope: roleScope,
      });
    }
  };

  const handleDeleteRole = () => {
    if (roleToDelete) {
      deleteRoleMutation.mutate(roleToDelete._id);
    }
  };

  const handleCreatePermission = () => {
    if (!permResource || !permAction || !permDescription) {
      toast.error("Please fill in all fields");
      return;
    }

    createPermissionMutation.mutate({
      resource: permResource,
      action: permAction,
      description: permDescription,
    });
  };

  const handleAssignRole = () => {
    if (!selectedUser || !selectedRole) {
      toast.error("Please select a user and role");
      return;
    }

    const role = roles.find(r => r.name === selectedRole);
    if (role?.scope === 'campus' && !selectedCampus) {
      toast.error("Campus-scoped roles require a campus selection");
      return;
    }

    assignRoleMutation.mutate({
      userId: selectedUser._id,
      role: selectedRole,
      campus_id: role?.scope === 'campus' ? selectedCampus : undefined,
    });
  };

  const togglePermission = (permCode: string) => {
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permCode)) {
        newSet.delete(permCode);
      } else {
        newSet.add(permCode);
      }
      return newSet;
    });
  };

  const toggleAllPermissionsInResource = (resource: string) => {
    const resourcePerms = permissions[resource]?.map(p => p.code) || [];
    const allSelected = resourcePerms.every(code => selectedPermissions.has(code));

    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        resourcePerms.forEach(code => newSet.delete(code));
      } else {
        resourcePerms.forEach(code => newSet.add(code));
      }
      return newSet;
    });
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower)
    );
  });

  // Filter users for selector dropdown
  const filteredUsersForSelector = users.filter((user) => {
    const searchLower = userSelectorSearch.toLowerCase();
    return (
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  // Filter permissions based on search term
  const filteredPermissions = Object.entries(permissions).reduce((acc, [resource, actions]) => {
    const searchLower = permissionSearchTerm.toLowerCase();
    if (!searchLower) return { ...acc, [resource]: actions };

    // Filter actions that match search term
    const matchingActions = actions.filter(action =>
      resource.toLowerCase().includes(searchLower) ||
      action.action.toLowerCase().includes(searchLower) ||
      action.code.toLowerCase().includes(searchLower) ||
      action.description.toLowerCase().includes(searchLower)
    );

    if (matchingActions.length > 0) {
      acc[resource] = matchingActions;
    }
    return acc;
  }, {} as typeof permissions);

  const getRoleBadgeVariant = (scope: string) => {
    return scope === 'global' ? 'default' : 'secondary';
  };

  const getRoleIcon = (roleName: string) => {
    if (roleName.includes('admin')) return Shield;
    return Users;
  };

  if (rolesLoading || permissionsLoading) {
    return (
      <DashboardLayout>
        <LoadingState message="Loading roles and permissions..." />
      </DashboardLayout>
    );
  }

  if (rolesError) {
    return (
      <DashboardLayout>
        <ErrorState message="Failed to load roles" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Roles & Permissions"
          description="Manage system roles, permissions, and user role assignments"
        />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="roles">Roles ({roles.length})</TabsTrigger>
            <TabsTrigger value="permissions">Permissions ({Object.keys(permissions).length})</TabsTrigger>
            <TabsTrigger value="users">Assign Roles</TabsTrigger>
          </TabsList>

          {/* Roles Tab */}
          <TabsContent value="roles" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={openCreateRoleDialog}>
                <Plus className="mr-2 h-4 w-4" />
                Create Role
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {roles.map((role) => {
                const RoleIcon = getRoleIcon(role.name);
                return (
                  <Card key={role._id} className="hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <RoleIcon className="h-5 w-5" />
                          <CardTitle className="text-lg">{role.display_name}</CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={getRoleBadgeVariant(role.scope)}>
                            {role.scope}
                          </Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                •••
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openEditRoleDialog(role)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Role
                              </DropdownMenuItem>
                              {!role.is_system_role && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setRoleToDelete(role);
                                    setDeleteConfirmOpen(true);
                                  }}
                                  className="text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete Role
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <CardDescription>{role.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="text-sm text-muted-foreground">
                          <strong>{role.permissions.length}</strong> permissions
                        </div>
                        {role.is_system_role && (
                          <Badge variant="outline" className="text-xs">
                            <Lock className="mr-1 h-3 w-3" />
                            System Role
                          </Badge>
                        )}
                        <div className="pt-2">
                          <details className="text-xs">
                            <summary className="cursor-pointer text-sm font-medium">View Permissions</summary>
                            <ul className="mt-2 space-y-1 pl-4 text-muted-foreground max-h-48 overflow-y-auto">
                              {role.permissions.map((perm, idx) => (
                                <li key={idx} className="list-disc">{perm}</li>
                              ))}
                            </ul>
                          </details>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search permissions by resource, action, code, or description..."
                  value={permissionSearchTerm}
                  onChange={(e) => setPermissionSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Button onClick={() => setPermissionDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Permission
              </Button>
            </div>

            {Object.keys(filteredPermissions).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No permissions found matching "{permissionSearchTerm}"
              </div>
            ) : (
              <div className="grid gap-4">
                {Object.entries(filteredPermissions).map(([resource, actions]) => (
                <Card key={resource}>
                  <CardHeader>
                    <CardTitle className="text-lg capitalize">{resource}</CardTitle>
                    <CardDescription>{actions.length} available actions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                      {actions.map((action) => (
                        <div
                          key={action.code}
                          className="flex items-center justify-between gap-2 rounded-lg border p-3 text-sm"
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <Badge variant="outline" className="capitalize">
                              {action.action}
                            </Badge>
                            <span className="text-muted-foreground text-xs">{action.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users by name, email, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Button onClick={() => setAssignRoleDialogOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Assign Role
              </Button>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Users ({filteredUsers.length})</CardTitle>
                <CardDescription>View and manage user roles</CardDescription>
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <div className="text-center py-8">
                    <LoadingState message="Loading users..." />
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No users found
                  </div>
                ) : (
                  <ScrollArea className="h-[600px]">
                    <div className="space-y-2">
                      {filteredUsers.map((user) => (
                        <div
                          key={user._id}
                          className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent/50 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="font-medium">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                            {user.campus_id && (
                              <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <GraduationCap className="h-3 w-3" />
                                Campus: {campuses.find((c: Campus) => c._id === user.campus_id)?.campus_name || user.campus_id}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={user.role?.includes('admin') ? 'default' : 'secondary'}>
                              {user.role || 'No Role'}
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedUser(user);
                                setAssignRoleDialogOpen(true);
                              }}
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create/Edit Role Dialog */}
        <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
              <DialogDescription>
                {editingRole ? 'Update role details and permissions' : 'Define a new role with specific permissions'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {!editingRole && (
                <div className="space-y-2">
                  <Label>Role Name (ID) *</Label>
                  <Input
                    placeholder="e.g., editor, moderator"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Unique identifier for the role (lowercase, underscores only)
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Display Name *</Label>
                <Input
                  placeholder="e.g., Content Editor"
                  value={roleDisplayName}
                  onChange={(e) => setRoleDisplayName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  placeholder="Describe what this role can do..."
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Scope *</Label>
                <Select value={roleScope} onValueChange={(value: 'global' | 'campus') => setRoleScope(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (all campuses)</SelectItem>
                    <SelectItem value="campus">Campus (specific campus only)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Permissions ({selectedPermissions.size} selected)</Label>
                <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
                  {Object.entries(permissions).map(([resource, actions]) => {
                    const resourcePerms = actions.map(a => a.code);
                    const allSelected = resourcePerms.every(code => selectedPermissions.has(code));
                    const someSelected = resourcePerms.some(code => selectedPermissions.has(code));

                    return (
                      <div key={resource} className="mb-4 last:mb-0">
                        <div className="flex items-center justify-between mb-2 pb-2 border-b">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={allSelected}
                              onCheckedChange={() => toggleAllPermissionsInResource(resource)}
                            />
                            <span className="font-medium capitalize">{resource}</span>
                            {someSelected && !allSelected && (
                              <Badge variant="secondary" className="text-xs">
                                {resourcePerms.filter(code => selectedPermissions.has(code)).length}/{resourcePerms.length}
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedResource(expandedResource === resource ? null : resource)}
                          >
                            {expandedResource === resource ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                        </div>
                        {expandedResource === resource && (
                          <div className="pl-6 space-y-2">
                            {actions.map((action) => (
                              <div key={action.code} className="flex items-start gap-2">
                                <Checkbox
                                  checked={selectedPermissions.has(action.code)}
                                  onCheckedChange={() => togglePermission(action.code)}
                                />
                                <div className="flex-1">
                                  <div className="text-sm font-medium">{action.code}</div>
                                  <div className="text-xs text-muted-foreground">{action.description}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setRoleDialogOpen(false);
                  resetRoleForm();
                  setEditingRole(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveRole}
                disabled={createRoleMutation.isPending || updateRoleMutation.isPending}
              >
                {createRoleMutation.isPending || updateRoleMutation.isPending
                  ? "Saving..."
                  : editingRole
                  ? "Update Role"
                  : "Create Role"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Permission Dialog */}
        <Dialog open={permissionDialogOpen} onOpenChange={setPermissionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Permission</DialogTitle>
              <DialogDescription>
                Add a new permission to the system
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Resource</Label>
                <Input
                  placeholder="e.g., posts, comments"
                  value={permResource}
                  onChange={(e) => setPermResource(e.target.value.toLowerCase())}
                />
              </div>

              <div className="space-y-2">
                <Label>Action</Label>
                <Select value={permAction} onValueChange={setPermAction}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create">Create</SelectItem>
                    <SelectItem value="read">Read</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                    <SelectItem value="manage">Manage (all)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  placeholder="e.g., Create new posts"
                  value={permDescription}
                  onChange={(e) => setPermDescription(e.target.value)}
                />
              </div>

              {permResource && permAction && (
                <div className="rounded-lg bg-accent p-3">
                  <p className="text-sm font-medium">Permission Code:</p>
                  <p className="text-sm text-muted-foreground">{permResource}:{permAction}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setPermissionDialogOpen(false);
                  resetPermissionForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreatePermission}
                disabled={createPermissionMutation.isPending}
              >
                {createPermissionMutation.isPending ? "Creating..." : "Create Permission"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Role Dialog */}
        <Dialog open={assignRoleDialogOpen} onOpenChange={setAssignRoleDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Role</DialogTitle>
              <DialogDescription>
                {selectedUser
                  ? `Assign a role to ${selectedUser.first_name} ${selectedUser.last_name}`
                  : 'Select a user to assign a role'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {!selectedUser && (
                <div className="space-y-2">
                  <Label>Select User</Label>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      const user = users.find(u => u._id === value);
                      setSelectedUser(user || null);
                      setUserSelectorSearch("");
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a user" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="p-2 border-b">
                        <Input
                          placeholder="Search users..."
                          value={userSelectorSearch}
                          onChange={(e) => setUserSelectorSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <ScrollArea className="h-[250px]">
                        {filteredUsersForSelector.length === 0 ? (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            No users found
                          </div>
                        ) : (
                          filteredUsersForSelector.map((user) => (
                            <SelectItem key={user._id} value={user._id}>
                              {user.first_name} {user.last_name} ({user.email})
                            </SelectItem>
                          ))
                        )}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {filteredUsersForSelector.length} of {users.length} users
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role._id} value={role.name}>
                        {role.display_name} ({role.scope})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedRole && roles.find(r => r.name === selectedRole)?.scope === 'campus' && (
                <div className="space-y-2">
                  <Label>Campus *</Label>
                  <Select value={selectedCampus} onValueChange={setSelectedCampus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select campus" />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-[200px]">
                        {campuses.map((campus: Campus) => (
                          <SelectItem key={campus._id} value={campus._id}>
                            {campus.campus_name}
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Select the campus for this campus-scoped role
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setAssignRoleDialogOpen(false);
                  setSelectedUser(null);
                  setSelectedRole("");
                  setSelectedCampus("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignRole}
                disabled={!selectedUser || !selectedRole || (roles.find(r => r.name === selectedRole)?.scope === 'campus' && !selectedCampus) || assignRoleMutation.isPending}
              >
                {assignRoleMutation.isPending ? "Assigning..." : "Assign Role"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Role</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the role "{roleToDelete?.display_name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setRoleToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteRole}
                disabled={deleteRoleMutation.isPending}
              >
                {deleteRoleMutation.isPending ? "Deleting..." : "Delete Role"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
